import moment from "moment";

export function stripeCardNumberValidation(cardNumber) {
    // Card type definitions with their validation rules
    const CARD_TYPES = {
        MASTERCARD: {
            pattern: /^(5[1-5][0-9]{14}|2[2-7][0-9]{14})$/,
            lengths: [16]
        },
        VISA: {
            pattern: /^4[0-9]{12}(?:[0-9]{3})?$/,
            lengths: [13, 16]
        },
        AMERICAN_EXPRESS: {
            pattern: /^3[47][0-9]{13}$/,
            lengths: [15]
        },
        DISCOVER: {
            pattern: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
            lengths: [16]
        },
        DINERS_CLUB: {
            pattern: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
            lengths: [14]
        },
        JCB: {
            pattern: /^(?:2131|1800|35\d{3})\d{11}$/,
            lengths: [16]
        }
    };

    /**
     * Validates card number using Luhn algorithm
     * @param {string} number - Card number to validate
     * @returns {boolean} - True if valid, false otherwise
     */
    const luhnCheck = (number) => {
        let sum = 0;
        let isEven = false;

        for (let i = number.length - 1; i >= 0; i--) {
            let digit = parseInt(number[i], 10);

            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }

            sum += digit;
            isEven = !isEven;
        }

        return sum % 10 === 0;
    };

    /**
     * Determines the card type based on the number
     * @param {string} number - Card number
     * @returns {string|null} - Card type or null if not found
     */
    const getCardType = (number) => {
        for (const [cardType, config] of Object.entries(CARD_TYPES)) {
            if (config.pattern.test(number)) {
                return cardType;
            }
        }
        return null;
    };

    /**
     * Validates card length for a specific card type
     * @param {string} number - Card number
     * @param {string} cardType - Type of card
     * @returns {boolean} - True if length is valid
     */
    const isValidLength = (number, cardType) => {
        return CARD_TYPES[cardType].lengths.includes(number.length);
    };

    // Start validation process
    try {
        // Clean the card number
        const cleanNumber = cardNumber.replace(/[^\d]/g, "").trim();

        // Basic validation
        if (!cleanNumber) {
            return "Card number is required";
        }

        // Get card type
        const cardType = getCardType(cleanNumber);
        if (!cardType) {
            return "Card type not accepted";
        }

        // Validate length
        if (!isValidLength(cleanNumber, cardType)) {
            return `Invalid length for ${cardType.replace(/_/g, ' ')} card`;
        }

        // Validate using Luhn algorithm
        if (!luhnCheck(cleanNumber)) {
            return "Invalid card number";
        }

        return undefined;

    } catch (error) {
        return "Invalid card number";
    }
}

export const stripeCardExpirValidation = (value) => {
    // Return early if no value provided
    if (!value) return;

    // Check if format matches MM/YY pattern
    if (!/^(0[1-9]|1[0-2])\/[0-9]{2}$/i.test(value.trim())) {
        return "Invalid date format";
    }

    const [monthStr, yearStr] = value.trim().split('/');
    const month = parseInt(monthStr, 10);
    const year = 2000 + parseInt(yearStr, 10);

    // Create date objects for comparison
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // Convert to 1-based month
    const currentYear = today.getFullYear();

    // If year is less than current year, invalid
    if (year < currentYear) {
        return "Please enter valid date";
    }

    // If same year but month is less than current month, invalid
    if (year === currentYear && month < currentMonth) {
        return "Please enter valid date";
    }

    return undefined;
};

export const textWithSpacesOnly = (value) => {
    if (value) {
        if (/^[a-zA-Z ]*$/i.test(value)) {
            return undefined;
        } else {
            return "Only alphabets";
        }
    } else {
        return undefined;
    }
};

export const minLength = (min) => (value) =>
    value && value.length < min ? `Must be 3 digits or more` : undefined;
